import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  Rate,
  Select,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BagIcon from "../../../assets/icons/BagIcon";
import ClockCircleIcon from "../../../assets/icons/ClockCircleIcon";
import DownArrowIcon from "../../../assets/icons/DownArrowIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import FlyIcon from "../../../assets/icons/FlyIcon";
import LocationIcon from "../../../assets/icons/LocationIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import VerifyIcon from "../../../assets/icons/VerifyIcon";
import UserImage from "../../../assets/images/avatar.svg";
import FileImage from "../../../assets/images/file.svg";
import PDFImage from "../../../assets/images/pdf.svg";
import CongratulationsModal from "../../../components/common/CongratulationsModal";
import TitleBar from "../../../components/common/TitleBar";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import Loader from "../../../components/ui/Loader";
import { useGetAllKPIGroupQuery } from "../../../features/organization/contractual/KPISetup/kpiSetupApi";
import { useGetAllSalarySlabQuery } from "../../../features/organization/contractual/SalarySlab/salarySlabApi";
import {
  useGetSingleJobAppliedQuery,
  useHireJobApplicationMutation,
} from "../../../features/organization/job/jobApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate, formatShortRelativeTime } from "../../../lib/time";
import { addCommaInNumber } from "../../../utils/number";
import { getExtension } from "../../../utils/string";
import { underscoreToCapitalizeString } from "../../../utils/utils";
import { validatePositiveNumber } from "../../../utils/validators";
import NotFound from "../../NotFound";

type ModalType = "CONFIRM" | "LOCATION" | "CONGRATULATION";
const backLink = "/organization/proposals";

export default function ProposalDetails() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [showFull, setShowFull] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const [activeKey, setActiveKey] = useState<
    string | number | (string | number)[]
  >("1");
  const { open, handleOpen, handleClose } = useModal<any, ModalType>(reRender);

  const { id } = useParams();

  // ------ Queries ------
  const { data, isError, error, isLoading } = useGetSingleJobAppliedQuery(
    Number(id),
    {
      skip: !id,
    }
  );

  const { data: allSalarySlabData, isLoading: allSalarySlabIsLoading } =
    useGetAllSalarySlabQuery({
      params: {
        page: 1,
        limit: 10000000000,
        status: "active",
      },
    });

  const {
    data: allKpiGroupData,
    isLoading: allKpiGroupIsLoading,
    isError: allKpiGroupIsError,
    error: allKpiGroupError,
  } = useGetAllKPIGroupQuery({
    params: {
      page: 1,
      limit: 10000000000,
      status: "active",
      for: "assign-kip",
    },
  });

  // ------ Mutations ------
  const [
    hireJobApplication,
    {
      isLoading: hireJobApplicationIsLoading,
      isError: hireJobApplicationIsError,
      error: hireJobApplicationError,
      isSuccess: hireJobApplicationIsSuccess,
    },
  ] = useHireJobApplicationMutation();

  // --- Error Handling ---
  useError(isError, error, "Get single job applied details fail");
  useError(
    hireJobApplicationIsError,
    hireJobApplicationError,
    "Hire job application fail"
  );
  useError(allKpiGroupIsError, allKpiGroupError, "Failed to fetch KPI groups");

  const singleData = data?.data;

  const handleChange = (key: any) => {
    setActiveKey(key);
  };

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      request_to_hire: true,
    };

    const newDataCallCenter = {
      contract_end_date: values?.contract_end_date
        ? dayjs(values?.contract_end_date).format("YYYY-MM-DD")
        : null,
      joining_date: values?.joining_date
        ? dayjs(values?.joining_date).format("YYYY-MM-DD")
        : null,
      contract_months: Number(values?.contract_months),
      slab_id: Number(values?.slab_id),
      kpi_group_id: Number(values?.kpi_group_id),
      payment_type: values?.payment_type,
      request_to_hire: true,
    };

    if (singleData?.jobApplication?.job?.job_category === "call_center") {
      handleOpen({
        type: "CONFIRM",
        data: newDataCallCenter,
      });
    } else {
      handleOpen({
        type: "CONFIRM",
        data: newData,
      });
    }
  };

  function reRender() {
    navigate(backLink);
  }

  const handleValuesChange = (changedValues: any, allValues: any) => {
    const { joining_date, contract_months } = allValues;

    if (joining_date && contract_months && contract_months > 0) {
      const endDate = dayjs(joining_date).add(contract_months, "month");
      form.setFieldsValue({ contract_end_date: endDate });
    } else {
      // Clear contract_end_date when either value is missing or invalid
      form.setFieldsValue({ contract_end_date: null });
    }
  };

  const disablePastDates = (current: any) =>
    current && current < dayjs().startOf("day");

  const handleSlabChange = (value: any) => {
    if (!value) {
      // When cleared
      form.setFieldsValue({
        fixed_amount: undefined,
        variable_amount: undefined,
      });
      return;
    }

    const selectedSlab = allSalarySlabData?.data?.salarySlabs?.find(
      (item: any) => item.id === value
    );

    if (selectedSlab) {
      form.setFieldsValue({
        fixed_amount: selectedSlab.fixed_amount,
        variable_amount: selectedSlab.variable_amount,
      });
    }
  };

  useEffect(() => {
    const jobApp = singleData?.jobApplication;
    const assignedSlab = jobApp?.active_assigned_slab;
    const assignedKpi = jobApp?.active_assigned_kpi_group;
    const contract = jobApp?.active_contract;

    if (
      assignedSlab?.salary_slab_id &&
      assignedKpi?.kpi_group_id &&
      contract?.contract_months &&
      contract?.payment_type &&
      contract?.contract_start_date &&
      contract?.contract_end_date
    ) {
      form.setFieldsValue({
        slab_id: assignedSlab.salary_slab_id,
        fixed_amount: assignedSlab.salary_slab?.fixed_amount,
        variable_amount: assignedSlab.salary_slab?.variable_amount,
        kpi_group_id: assignedKpi.kpi_group_id,
        contract_months: contract.contract_months,
        payment_type: contract.payment_type,
        joining_date: dayjs(contract.contract_start_date),
        contract_end_date: dayjs(contract.contract_end_date),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form,
    singleData?.jobApplication?.active_assigned_slab,
    singleData?.jobApplication?.active_assigned_kpi_group,
    singleData?.jobApplication?.active_contract,
  ]);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      const isClamped = el?.scrollHeight > el?.clientHeight;
      setIsOverflowing(isClamped);
    }
  }, [singleData?.jobApplication?.coverLetter]);

  useEffect(() => {
    if (hireJobApplicationIsSuccess) {
      handleOpen({ type: "CONGRATULATION" });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hireJobApplicationIsSuccess]);

  useEffect(() => {
    if (!!singleData?.jobApplication?.final_price) {
      form.setFieldsValue({
        final_price: singleData?.jobApplication?.final_price,
      });
    }
  }, [form, singleData?.jobApplication?.final_price]);

  // const maxBudget = Number(singleData?.jobApplication?.job?.max_budget);

  if (
    error &&
    typeof error === "object" &&
    "status" in error &&
    (error as { status?: number }).status === 404
  ) {
    return <NotFound />;
  }

  console.log(
    singleData?.jobApplication?.is_hired === 0 &&
      singleData?.jobApplication?.applicant_confirm_status !== null
  );

  return (
    <SidebarLayout title="Proposals">
      {open?.type === "CONFIRM" && (
        <ConfirmModal
          title={"Do you want to hire this person?"}
          subTitle={
            // open?.data?.final_price > maxBudget
            //   ? "The price exceeds your maximum budget. Do you still want to proceed with hiring this person?"
            //   :

            "Are you sure you want to proceed with hiring this person?"
          }
          handleClose={() => handleClose()}
          open={open?.open}
          data={open?.data}
          okLoading={hireJobApplicationIsLoading}
          handleOk={(values) => {
            if (id) {
              const newData = {
                id: Number(id),
                data: values,
              };

              hireJobApplication(newData);
              // console.log("Values", newData);
            }
          }}
          okText={"Yes, Hire"}
        />
      )}

      {open?.type === "CONGRATULATION" && (
        <CongratulationsModal
          open={open?.open}
          handleClose={() => handleClose(true)}
          text={
            "You’ve successfully hired the candidate. They will be notified shortly."
          }
        />
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <TitleBar title={`Proposals Details - ${id}`} backLink={backLink} />

          <div className="grid md:grid-cols-12 gap-[18px]">
            {/* Left Side – 60% */}
            <div className="md:col-span-7">
              {/* Proposal Details Card */}
              <Card>
                <div className="flex items-start justify-between border-b border-[#D7DFE9] pb-2 mb-2">
                  <div className="flex items-start justify-start gap-4">
                    <div>
                      <img
                        className="w-11 h-11 rounded-[100px]"
                        src={
                          singleData?.jobApplication?.applicant?.profile
                            ?.pro_pic || UserImage
                        }
                        alt="user_image"
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-x-4 flex-wrap">
                        <h4 className="text-base font-medium text-header">
                          {singleData?.jobApplication?.applicant?.name}
                        </h4>
                        {singleData?.jobApplication?.applicant?.is_verified ===
                          1 && (
                          <span className="text-xs font-medium flex items-center gap-1.5 text-primary">
                            <VerifyIcon />
                            Verified
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1 text-sm text-body-text font-medium my-1">
                        <LocationIcon />
                        {singleData?.jobApplication?.applicant?.profile
                          ?.district || "N/A"}
                      </div>

                      <div className="flex items-center flex-wrap gap-x-3">
                        <Rate
                          disabled
                          allowHalf
                          value={0}
                          style={{ color: "#FFC700" }}
                        />

                        {/* <div>
                          <span className="text-header text-base font-medium inline-block mr-1">
                            4.8
                          </span>
                          <span className="text-header text-sm font-normal inline-block">
                            (251)
                          </span>
                        </div> */}
                      </div>
                    </div>
                  </div>

                  {/* <div>
                    <Button
                      size="small"
                      className="w-full !bg-[#11A75C] !text-white border border-[#11A75C] hover:!bg-[#11A75C] hover:!border-[#11A75C]"
                      icon={<DoubleMessageIcon />}
                    >
                      Message
                    </Button>
                  </div> */}
                </div>

                <div className="flex items-start justify-between">
                  <div>
                    <div className="self-stretch justify-start text-slate-500 text-xs font-normal">
                      {singleData?.jobApplication?.created_at
                        ? formatDate(singleData?.jobApplication?.created_at)
                        : "-"}
                    </div>
                    <div className="self-stretch justify-start text-zinc-700 text-sm font-bold">
                      {singleData?.jobApplication?.created_at
                        ? formatShortRelativeTime(
                            singleData?.jobApplication?.created_at
                          )
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="self-stretch justify-start text-slate-500 text-xs font-normal">
                      Price
                    </div>
                    <div className="self-stretch justify-start text-zinc-700 text-sm font-bold">
                      {/* {singleData?.jobApplication?.price}/month */}
                      {singleData?.jobApplication?.price}/
                      {underscoreToCapitalizeString(
                        singleData?.jobApplication?.job?.contract_type ?? ""
                      )}
                    </div>
                  </div>

                  {!!singleData?.jobApplication?.final_price && (
                    <div>
                      <div className="self-stretch justify-start text-slate-500 text-xs font-normal">
                        Final Price
                      </div>
                      <div className="self-stretch justify-start text-zinc-700 text-sm font-bold">
                        {singleData?.jobApplication?.final_price}/
                        {underscoreToCapitalizeString(
                          singleData?.jobApplication?.job?.contract_type ?? ""
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="self-stretch justify-start text-slate-500 text-xs font-normal">
                      Location
                    </div>
                    <div className="self-stretch justify-start text-zinc-700 text-sm font-bold">
                      {singleData?.jobApplication?.location}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Cover Letter Card */}
              <Card className="mt-4">
                <div className="text-zinc-700 text-lg font-medium mb-4">
                  Cover Letter
                </div>

                <div
                  ref={contentRef}
                  className={`text-slate-700 text-sm font-normal whitespace-pre-line transition-all duration-300 ${
                    showFull ? "" : "line-clamp-6"
                  }`}
                >
                  {singleData?.jobApplication?.coverLetter}
                </div>

                {isOverflowing && (
                  <div
                    onClick={() => setShowFull(!showFull)}
                    className="mt-2 cursor-pointer text-blue-600 text-sm font-normal w-fit"
                  >
                    {showFull ? "View Less" : "View More"}
                  </div>
                )}
              </Card>

              {/* Job File Card */}
              {!!singleData?.jobApplication?.file && (
                <Card className="mt-4">
                  <div className="text-zinc-700 text-lg font-medium mb-4">
                    Job File
                  </div>
                  <div className="flex gap-0.5 items-end">
                    {/* <Image
                      src={FileImage}
                      alt="job_file"
                      width={80}
                      height={80}
                      preview={false}
                      className="object-contain"
                    /> */}

                    {(() => {
                      const file = singleData?.jobApplication?.file || "";
                      const extension = getExtension(file);

                      if (extension === "pdf") {
                        return (
                          <Image
                            src={PDFImage}
                            width={60}
                            height={70}
                            preview={false}
                            alt="pdf"
                          />
                        );
                      }

                      if (
                        ["png", "jpg", "jpeg", "webp", "svg"].includes(
                          extension
                        )
                      ) {
                        return (
                          <Image
                            src={file}
                            width={60}
                            height={70}
                            alt="image"
                          />
                        );
                      }

                      return (
                        <Image
                          src={FileImage}
                          width={60}
                          height={70}
                          preview={false}
                          alt="file"
                        />
                      );
                    })()}
                    <span className="text-accent">
                      <a
                        href={singleData?.jobApplication?.file}
                        target="_blank"
                        className="-mt-[2px] cursor-pointer"
                        rel="noreferrer"
                        download="Job File"
                      >
                        <DownloadIcon width={14} height={14} />
                      </a>
                    </span>
                  </div>
                </Card>
              )}

              {/* Additional Questions Card */}
              {/* Using Collapse for additional questions */}
              <Card className="mt-4">
                <div className="text-zinc-700 text-lg font-medium mb-4">
                  Additional Questions
                </div>
                {singleData?.jobApplication?.questionsAnswers?.map(
                  (item, index) => (
                    <div className="mt-2 mb-2" key={index}>
                      <Collapse
                        activeKey={activeKey}
                        onChange={handleChange}
                        expandIconPosition="end"
                        expandIcon={({ isActive }) =>
                          isActive ? (
                            <span
                              style={{
                                transform: isActive
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.3s ease",
                              }}
                            >
                              <DownArrowIcon />
                            </span>
                          ) : (
                            <DownArrowIcon />
                          )
                        }
                        items={[
                          {
                            key: `${index + 1}`,
                            label: item?.question,
                            children: <p>{item?.answer}</p>,
                          },
                        ]}
                      />
                    </div>
                  )
                )}
              </Card>

              {/* Slot Card */}
              {/* This card is for displaying the slot details */}
              {singleData?.jobApplication?.job?.job_category === "rooster" && (
                <Card className="mt-4">
                  <div className="justify-center text-zinc-700 text-lg font-medium mb-2">
                    Slot
                  </div>

                  <div className="flex justify-between items-start border-b border-[#D7DFE9] pb-2 mb-4">
                    <div>
                      <div className="justify-center text-slate-700 text-sm font-normal">
                        Date
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        22 May, 2025
                      </div>
                    </div>
                    <div>
                      <div className="justify-center text-slate-700 text-sm font-normal">
                        Slot
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        9:00 AM - 12:00 PM
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        9:00 AM - 12:00 PM
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        9:00 AM - 12:00 PM
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <div className="justify-center text-slate-700 text-sm font-normal">
                        Date
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        22 May, 2025
                      </div>
                    </div>
                    <div>
                      <div className="justify-center text-slate-700 text-sm font-normal">
                        Slot
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        9:00 AM - 12:00 PM
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        9:00 AM - 12:00 PM
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        9:00 AM - 12:00 PM
                      </div>
                    </div>

                    <div>
                      <div className="justify-center text-slate-700 text-sm font-normal">
                        Seat
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        Seat 6
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        Seat 6
                      </div>
                      <div className="justify-start text-zinc-700 text-sm font-medium">
                        Seat 6
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Right Side – 40% */}
            <div className="md:col-span-5">
              {/* Job Details Card */}
              <Card className="mb-4">
                <div className="text-zinc-700 text-lg font-medium border-b border-[#D7DFE9] pb-2 mb-2">
                  Job Details
                </div>

                <div className="text-fuchsia-600 text-sm font-medium mb-2">
                  {singleData?.jobApplication?.job?.job_title}
                </div>

                <div className="flex items-center justify-start gap-3">
                  <div>
                    <BagIcon />
                  </div>
                  <div className="text-slate-600 text-xs font-normal">
                    {underscoreToCapitalizeString(
                      singleData?.jobApplication?.job?.job_category ?? ""
                    )}
                  </div>

                  {singleData?.jobApplication?.job?.job_type && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        {singleData?.jobApplication?.job?.job_type}
                      </div>
                    </>
                  )}

                  {singleData?.jobApplication?.job?.contract_type ===
                    "fixed_term" && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        BDT{" "}
                        {addCommaInNumber(
                          Number(
                            singleData?.jobApplication?.job
                              ?.fixed_term_price_per_month || 0
                          )
                        )}{" "}
                        - {singleData?.jobApplication?.job?.fixed_term_duration}{" "}
                        /{" "}
                        {underscoreToCapitalizeString(
                          singleData?.jobApplication?.job?.contract_type
                        )}
                      </div>
                    </>
                  )}

                  {singleData?.jobApplication?.job?.contract_type ===
                    "hourly" && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        BDT{" "}
                        {addCommaInNumber(
                          Number(
                            singleData?.jobApplication?.job?.hourly_price || 0
                          )
                        )}{" "}
                        /{" "}
                        {underscoreToCapitalizeString(
                          singleData?.jobApplication?.job?.contract_type ?? ""
                        )}
                      </div>
                    </>
                  )}

                  {singleData?.jobApplication?.job?.contract_type ===
                    "open_bid" && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        BDT{" "}
                        {addCommaInNumber(
                          Number(
                            singleData?.jobApplication?.job
                              ?.open_bid_max_budget || 0
                          )
                        )}{" "}
                        /{" "}
                        {underscoreToCapitalizeString(
                          singleData?.jobApplication?.job?.contract_type ?? ""
                        )}
                      </div>
                    </>
                  )}

                  {singleData?.jobApplication?.job?.contract_type ===
                    "milestone" && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        BDT{" "}
                        {addCommaInNumber(
                          Number(
                            singleData?.jobApplication?.job?.milestone_price ||
                              0
                          )
                        )}{" "}
                        /{" "}
                        {underscoreToCapitalizeString(
                          singleData?.jobApplication?.job?.contract_type ?? ""
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-start gap-3 mt-2">
                  <div>
                    <FlyIcon />
                  </div>
                  <div className="justify-center text-slate-600 text-xs font-normal">
                    Proposals:{" "}
                    {singleData?.jobApplication?.job
                      ?.number_of_people_applied || 0}
                  </div>
                </div>

                {/* <div className="flex items-center justify-start gap-3 mt-2">
                  <div>
                    <PayCardIcon />
                  </div>
                  <div className="justify-center text-slate-600 text-xs font-normal">
                    Payment verified
                  </div>
                  <div>
                    <GreenCircleCorrectIcon />
                  </div>
                </div> */}

                <div className="self-stretch justify-center text-slate-700 text-sm font-normal leading-tight tracking-tight mt-4">
                  {singleData?.jobApplication?.job?.job_description}
                </div>

                <div className="self-stretch justify-center text-slate-700 text-sm font-normal leading-tight tracking-tight mt-4">
                  {singleData?.jobApplication?.job?.penalty_clause}
                </div>

                <div className="self-stretch justify-center text-slate-700 text-sm font-normal leading-tight tracking-tight mt-4">
                  <span className="font-medium">Requirement Skills:</span>{" "}
                  {singleData?.jobApplication?.job?.skills?.join(", ")}
                </div>

                <div className="flex items-center justify-start gap-3 mt-3">
                  <div>
                    <ClockCircleIcon />
                  </div>
                  <div className="justify-center text-slate-600 text-xs font-normal">
                    Posted{" "}
                    {singleData?.jobApplication?.job?.created_at
                      ? formatShortRelativeTime(
                          singleData?.jobApplication?.job?.created_at
                        )
                      : "-"}
                  </div>
                </div>
              </Card>

              {/* Deals Card */}
              {/* This card is for confirming the hire and finalizing the deal */}
              <Form
                className="custom-form"
                layout="vertical"
                requiredMark="optional"
                size="large"
                onFinish={onFinish}
                form={form}
                onValuesChange={handleValuesChange}
              >
                <Card>
                  <div className="text-zinc-700 text-lg font-medium border-b border-[#D7DFE9] pb-2 mb-2">
                    Deals
                  </div>
                  {singleData?.jobApplication?.job?.job_category ===
                  "call_center" ? (
                    <div>
                      <Form.Item
                        name="slab_id"
                        label="Salary Slab"
                        className="mb-4"
                        rules={[
                          {
                            required: true,
                            message: "Please enter select slab",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select Slab"
                          size="large"
                          allowClear
                          showSearch
                          suffixIcon={<SelectDropdownLgIcon />}
                          onChange={handleSlabChange}
                          options={allSalarySlabData?.data?.salarySlabs?.map(
                            (item: any) => ({
                              label: item.slab_name,
                              value: item.id,
                            })
                          )}
                          disabled={
                            singleData?.jobApplication?.is_hired >= 0 &&
                            singleData?.jobApplication
                              ?.applicant_confirm_status !== null
                          }
                        />
                      </Form.Item>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          name="fixed_amount"
                          label="Fixed Amount"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please enter fixed amount",
                            },
                            { validator: validatePositiveNumber },
                          ]}
                        >
                          <Input suffix="BDT" placeholder="0.00" disabled />
                        </Form.Item>
                        <Form.Item
                          name="variable_amount"
                          label="Variable Amount"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please enter variable amount",
                            },
                            { validator: validatePositiveNumber },
                          ]}
                        >
                          <Input suffix="BDT" placeholder="0.00" disabled />
                        </Form.Item>
                      </div>

                      <Form.Item
                        name="kpi_group_id"
                        label="KPI Group"
                        className="mb-4"
                        rules={[
                          {
                            required: true,
                            message: "Please select a KPI group",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select a Group"
                          size="large"
                          allowClear
                          showSearch
                          suffixIcon={<SelectDropdownLgIcon />}
                          // onChange={handleGroupChange}
                          options={
                            allKpiGroupData?.data?.groups?.map((item: any) => ({
                              label: item.group_name,
                              value: item.id,
                            })) || []
                          }
                          disabled={
                            singleData?.jobApplication?.is_hired >= 0 &&
                            singleData?.jobApplication
                              ?.applicant_confirm_status !== null
                          }
                        />
                      </Form.Item>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          name="payment_type"
                          label="Payment Type"
                          className="mb-4"
                          initialValue="monthly"
                          rules={[
                            {
                              required: true,
                              message: "Please select payment type",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select Payment Type"
                            size="large"
                            allowClear
                            showSearch
                            suffixIcon={<SelectDropdownLgIcon />}
                            options={[
                              { label: "Monthly", value: "monthly" },
                              // { label: "Weekly", value: "weekly" },
                              // { label: "Daily", value: "daily" },
                            ]}
                            disabled={
                              singleData?.jobApplication?.is_hired >= 0 &&
                              singleData?.jobApplication
                                ?.applicant_confirm_status !== null
                            }
                          />
                        </Form.Item>
                        <Form.Item
                          name="joining_date"
                          label="Joining Date"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please select joining date",
                            },
                          ]}
                        >
                          <DatePicker
                            className="w-full"
                            format="DD/MM/YYYY"
                            placeholder="dd/mm/yyyy"
                            disabledDate={disablePastDates}
                            disabled={
                              singleData?.jobApplication?.is_hired >= 0 &&
                              singleData?.jobApplication
                                ?.applicant_confirm_status !== null
                            }
                          />
                        </Form.Item>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          name="contract_months"
                          label="Contract (Month)"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please enter contract month",
                            },
                            {
                              validator: (_, value) =>
                                value && value <= 0
                                  ? Promise.reject("Must be positive number")
                                  : Promise.resolve(),
                            },
                          ]}
                        >
                          <Input
                            placeholder="e.g. 5"
                            // onChange={handleContractMonthsChange}
                            allowClear
                            type="number"
                            disabled={
                              singleData?.jobApplication?.is_hired > 0 ||
                              (singleData?.jobApplication?.is_hired >= 0 &&
                                singleData?.jobApplication
                                  ?.applicant_confirm_status !== null)
                            }
                          />
                        </Form.Item>

                        <Form.Item
                          name="contract_end_date"
                          label="Contract End Date"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please select contract end date",
                            },
                          ]}
                        >
                          <DatePicker
                            className="w-full"
                            format="DD/MM/YYYY"
                            placeholder="dd/mm/yyyy"
                            disabledDate={disablePastDates}
                            disabled
                          />
                        </Form.Item>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Form.Item
                        name="final_price"
                        label="Final Price"
                        className="mb-4"
                        rules={[
                          {
                            required: true,
                            message: "Please enter final price",
                          },
                          {
                            validator(_, value) {
                              if (
                                value === undefined ||
                                value === null ||
                                value === ""
                              ) {
                                return Promise.resolve();
                              }

                              const num = Number(value);

                              if (isNaN(num) || !Number.isInteger(num)) {
                                return Promise.reject(
                                  new Error("Only integer number are allowed")
                                );
                              }

                              if (num <= 0) {
                                return Promise.reject(
                                  new Error("Price must be a positive number")
                                );
                              }

                              // if (num > maxBudget) {
                              //   return Promise.reject(
                              //     new Error(
                              //       "Price cannot exceed the maximum budget"
                              //     )
                              //   );
                              // }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          size="large"
                          className="w-full"
                          placeholder="Enter final price"
                          readOnly={
                            !!singleData?.jobApplication?.request_to_hire
                          }
                          disabled={
                            !!singleData?.jobApplication?.request_to_hire ||
                            (singleData?.jobApplication?.job?.contract_type ===
                              "open_bid" &&
                              !!singleData?.jobApplication?.final_price)
                          }
                          // max={
                          //   Number(singleData?.jobApplication?.job?.max_budget) ||
                          //   0
                          // }
                        />
                      </Form.Item>
                    </div>
                  )}

                  <div className="self-stretch justify-center text-slate-700 text-sm font-normal leading-tight tracking-tight mt-4 mb-4">
                    By confirming this hire, you officially agree to engage with
                    the selected freelancer under the platform’s terms,
                    including project scope, payment process, and communication
                    policies. Once hired, all collaborations should follow our
                    guidelines to ensure a smooth, dispute-free experience for
                    both parties. Your commitment helps us keep the work
                    environment transparent and professional.
                  </div>

                  {singleData?.jobApplication?.is_hired === 0 &&
                  singleData?.jobApplication?.applicant_confirm_status ===
                    null ? (
                    <Button
                      htmlType="submit"
                      size="large"
                      className="w-full"
                      type="primary"
                    >
                      Confirm & Hire Now
                    </Button>
                  ) : (
                    <Button
                      size="large"
                      className="w-full disabled:opacity-70"
                      type="primary"
                      disabled
                    >
                      {!!singleData?.jobApplication?.is_hired
                        ? "Hired"
                        : !singleData?.jobApplication?.is_hired &&
                          singleData?.jobApplication
                            ?.applicant_confirm_status === "pending"
                        ? "Waiting for Bondhu's Confirmation"
                        : !singleData?.jobApplication?.is_hired &&
                          singleData?.jobApplication
                            ?.applicant_confirm_status === "rejected"
                        ? "Bondhu rejected your hire request"
                        : "Not Hired"}
                    </Button>
                  )}

                  <div className="text-center text-slate-400 text-xs font-medium mt-4">
                    Protected by platform policies
                  </div>
                </Card>
              </Form>
            </div>
          </div>
        </>
      )}
    </SidebarLayout>
  );
}
